import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

/**
 * StorageService — substitui localStorage por cookies.
 * Centraliza toda persistência client-side para facilitar
 * troca de estratégia (cookie, sessionStorage, etc).
 *
 * Segurança:
 * - Cookies com flag Secure (HTTPS only em produção)
 * - SameSite=Strict para prevenir CSRF
 */
@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly defaultPath = '/';
  private readonly defaultExpireDays = 7;
  private readonly isSecure = location.protocol === 'https:';

  constructor(private cookie: CookieService) {}

  get<T>(key: string): T | null {
    const raw = this.cookie.get(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  }

  set(key: string, value: unknown, expireDays?: number): void {
    const days = expireDays ?? this.defaultExpireDays;
    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    this.cookie.set(key, serialized, days, this.defaultPath, undefined, this.isSecure, 'Strict');
  }

  remove(key: string): void {
    this.cookie.delete(key, this.defaultPath);
  }

  has(key: string): boolean {
    return this.cookie.check(key);
  }

  clearAll(): void {
    this.cookie.deleteAll(this.defaultPath);
  }
}
